import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_GROW_ANIMATION } from '../chartAnimation';
import ClrRechartsTooltip from '../ClrRechartsTooltip';
import { buildNiceAxisScale, CLR_VALUE_Y_AXIS_WIDTH, getSeriesMax } from '../axisScale';
import { clrHoverColor } from '../chartMarkHover';

const CREATED_COLOR = '#2E8B6F';
const CLOSED_COLOR = '#6FA287';
const CREATED_DOT_HOVER = clrHoverColor(CREATED_COLOR);
const CLOSED_DOT_HOVER = clrHoverColor(CLOSED_COLOR);

function ClosureOverviewCard({ data }) {
  const chartData = useMemo(() => (data || []).map((row) => ({
    month: row.month_label || row.monthLabel,
    monthKey: row.month_key || row.monthKey,
    created: row.created_count ?? row.createdCount ?? 0,
    closed: row.closed_count ?? row.closedCount ?? 0,
  })), [data]);

  const axis = useMemo(() => {
    const maxValue = getSeriesMax(chartData, ['created', 'closed']);
    return buildNiceAxisScale(maxValue);
  }, [chartData]);

  const formatTooltip = (payload, label) => {
    const month = payload[0]?.payload?.month || label || '';
    return {
      title: month,
      rows: [
        {
          key: 'created',
          label: 'Created',
          value: payload.find((p) => p.dataKey === 'created')?.value ?? 0,
          color: CREATED_COLOR,
        },
        {
          key: 'closed',
          label: 'Closed',
          value: payload.find((p) => p.dataKey === 'closed')?.value ?? 0,
          color: CLOSED_COLOR,
          dashed: true,
        },
      ],
    };
  };

  return (
    <div className="clr-chart-card">
      <div className="clr-chart-card-header clr-chart-card-header-accent">
        <h3 className="clr-chart-card-title">Referral closure overview</h3>
      </div>
      <div className="clr-chart-card-body">
        {chartData.length === 0 ? (
          <p className="clr-chart-plot clr-chart-empty">No closure data</p>
        ) : (
        <div className="clr-chart-plot">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e4e0" />
              <XAxis
                dataKey="month"
                height={28}
                tickMargin={4}
                interval={0}
                angle={-35}
                textAnchor="end"
                tick={{ fontSize: 9, fill: '#5F7470', fontWeight: 400 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                width={CLR_VALUE_Y_AXIS_WIDTH}
                tick={{ fontSize: 11, fill: '#6b7a74' }}
                axisLine={false}
                tickLine={false}
                domain={[0, axis.domainMax]}
                ticks={axis.ticks}
              />
              <ClrRechartsTooltip
                shared
                clampToCard
                formatter={(payload) => formatTooltip(payload)}
              />
              <ReferenceLine y={0} stroke="#e0e4e0" strokeWidth={1} />
              <Line
                type="monotone"
                dataKey="created"
                name="Created"
                stroke={CREATED_COLOR}
                strokeWidth={2}
                dot={{ r: 4, fill: CREATED_COLOR, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: CREATED_DOT_HOVER, strokeWidth: 0 }}
                {...CHART_GROW_ANIMATION}
              />
              <Line
                type="monotone"
                dataKey="closed"
                name="Closed"
                stroke={CLOSED_COLOR}
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={{ r: 4, fill: CLOSED_COLOR, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: CLOSED_DOT_HOVER, strokeWidth: 0 }}
                {...CHART_GROW_ANIMATION}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        )}
      </div>
    </div>
  );
}

ClosureOverviewCard.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    month_label: PropTypes.string,
    monthLabel: PropTypes.string,
    created_count: PropTypes.number,
    createdCount: PropTypes.number,
    closed_count: PropTypes.number,
    closedCount: PropTypes.number,
  })),
};

export default ClosureOverviewCard;
